import React, { useState, useRef, useEffect } from "react";

interface Option {
  _id: string;
  CategoryName: string;
  otherName?: string;
}

interface DropdownSelectProps {
  value: string | number;
  title?: string;
  name: string;
  options: Option[];
  onChange: (value: string) => void;
  titleofSelect?: string;
  mandatory?: boolean;
  defultSelect?: boolean;
  className?: string;
  disabled?: boolean;
}

const CustomDropdownSelect: React.FC<DropdownSelectProps> = ({
  value,
  title,
  name,
  options,
  onChange,
  titleofSelect,
  mandatory = false,
  defultSelect = false,
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInitRef = useRef(true);

  // Truncate text to max 20 characters
  const truncateText = (text: string, maxLength: number = 18) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  useEffect(() => {
    if (!defultSelect) return;

    if (isInitRef.current && options.length > 0) {
      isInitRef.current = false;

      if (!options.some((opt) => opt._id === value)) {
        onChange(options[0]._id);
      }
    }
  }, [options, defultSelect]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt._id === value);

  return (
    <div className={`mb-3 ${className} ${disabled ? "opacity-60 cursor-not-allowed w-full" : ""}`} ref={dropdownRef}>
      {title && (
        <div className='flex'>
          <p className='text-sm font-medium mb-1'>{title}</p>
          {mandatory && <p className='text-red-600'>*</p>}
        </div>
      )}

      <div className='relative w-full'>
        <div
          className={`w-full border border-gray-300 rounded px-2 py-2 text-sm cursor-pointer
            ${disabled ? "bg-gray-100 text-gray-500" : "bg-white"}
          `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className='flex justify-between items-center'>
            <div className='flex flex-col'>
              {/* Display selected option with full name as title attribute for hover */}
              {selectedOption ? (
                <>
                  <span title={selectedOption.CategoryName}>{truncateText(selectedOption.CategoryName)}</span>
                  {/* Display full category name as a subtitle */}
                </>
              ) : (
                titleofSelect || `Select ${title}`
              )}
            </div>
            <span className='transform -translate-y-0'>▼</span>
          </div>
        </div>

        {isOpen && !disabled && (
          <div className='absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg max-h-[300px] overflow-y-auto'>
            {titleofSelect && (
              <div
                className={`px-2 py-2 cursor-pointer hover:bg-gray-100 ${value === "" ? "bg-blue-50 text-blue-600" : ""}`}
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
              >
                <div className='flex flex-col'>
                  <span className='text-gray-500'>{titleofSelect}</span>
                </div>
              </div>
            )}
            {options.map((opt) => (
              <div
                key={opt._id}
                className={`px-2 py-2 cursor-pointer hover:bg-gray-100 ${opt._id === value ? "bg-blue-50 text-blue-600" : ""}`}
                onClick={() => {
                  onChange(opt._id);
                  setIsOpen(false);
                }}
                title={opt.CategoryName} // Full name on hover
              >
                <div className='flex flex-col'>
                  <span>{opt.CategoryName}</span>
                  <p className='text-sm text-slate-500'>{opt.otherName}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden select for form compatibility */}
      <select name={name} value={value} onChange={(e) => onChange(e.target.value)} className='hidden' disabled={disabled}>
        {options.map((opt) => (
          <option key={opt._id} value={opt._id}>
            {opt.CategoryName}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CustomDropdownSelect;
